import {ReactNode} from "react";
import {Button, FormControl, Grid, InputLabel, MenuItem, Select, Stack, TextField} from "@mui/material";
import Section from "../Section";
import {labelStyle} from "../../styles";
import {defineMessage, FormattedMessage} from "react-intl";
import MACAddressForm from "./MACAddressForm";
import {ThreeDots} from "@dvrt/graphql-widgets";
import BackendError from "../BackendError";
import ErrorHelper from "../../utils/ErrorHelper";
import MultipleLineError from "../MultipleLineError";
import {useVendorsQuery} from "@/queries.generated";

export default function MACAddressManagement() {
    const form = new MACAddressForm();
    const errorHelper = new ErrorHelper(form.errors);

    const {data, loading, error} = useVendorsQuery({
        onCompleted(_data) {
            form.updateValues({vendorId: _data.vendors[0].id});
        },
    });

    if (loading) return <ThreeDots height={5} />;

    if (error) return <BackendError error={error} />;

    function createVendors(): ReactNode {
        const nodes = [];
        for (const vendor of data.vendors) {
            nodes.push(
                <MenuItem key={vendor.id} value={vendor.id}>
                    {vendor.name}
                </MenuItem>,
            );
        }
        return nodes;
    }

    return (
        <Stack spacing={2}>
            <Section
                icon="pen-alt"
                label={defineMessage({id: "mac_address_management", defaultMessage: "MAC address management"})}
            >
                <Grid container>
                    <Grid style={labelStyle} size={2}>
                        <TextField
                            variant="standard"
                            label={
                                <FormattedMessage
                                    id="e164_or_short_number"
                                    defaultMessage="E.164 or <identifier>@<domain>"
                                />
                            }
                            value={form.values.phoneNumber}
                            onChange={(event) => form.updateValues({phoneNumber: event.target.value})}
                            error={errorHelper.hasError("phoneNumber")}
                            helperText={<MultipleLineError errors={errorHelper.fieldErrors("phoneNumber")} />}
                            style={{display: "flex"}}
                        />
                    </Grid>
                </Grid>
                <Grid container>
                    <Grid style={labelStyle} size={2}>
                        <TextField
                            variant="standard"
                            label={<FormattedMessage id="current_mac_address" defaultMessage="Current MAC address" />}
                            value={form.values.currentMacAddress}
                            onChange={(event) => form.updateValues({currentMacAddress: event.target.value})}
                            error={errorHelper.hasError("currentMacAddress")}
                            helperText={<MultipleLineError errors={errorHelper.fieldErrors("currentMacAddress")} />}
                            style={{display: "flex"}}
                        />
                    </Grid>
                </Grid>
                <Grid container>
                    <Grid style={labelStyle} size={2}>
                        <TextField
                            variant="standard"
                            label={<FormattedMessage id="new_mac_address" defaultMessage="New MAC address" />}
                            value={form.values.newMacAddress}
                            onChange={(event) => form.updateValues({newMacAddress: event.target.value})}
                            error={errorHelper.hasError("newMacAddress")}
                            helperText={<MultipleLineError errors={errorHelper.fieldErrors("newMacAddress")} />}
                            style={{display: "flex"}}
                        />
                    </Grid>
                </Grid>
                <Grid container>
                    <Grid style={labelStyle} size={2}>
                        <FormControl variant="standard">
                            <InputLabel id="vendor-select-label">
                                <FormattedMessage id="vendor" defaultMessage="Vendor" />
                            </InputLabel>
                            <Select
                                variant="standard"
                                labelId="vendor-select-label"
                                value={form.values.vendorId}
                                onChange={(event) => form.updateValues({vendorId: Number(event.target.value)})}
                            >
                                {createVendors()}
                            </Select>
                        </FormControl>
                    </Grid>
                </Grid>
                <Grid container style={{marginTop: "2rem"}}>
                    <Grid style={labelStyle} size={2}>
                        <Button variant="contained" color="primary" onClick={() => form.submit()}>
                            <FormattedMessage id="save" defaultMessage="Save" />
                        </Button>
                    </Grid>
                </Grid>
            </Section>
        </Stack>
    );
}
